import openpgp, * as oModule from 'openpgp'
import Lame from 'lamejs'

import FileSystem from '@/models/FileSystem'
import Browser from '@/utils/Browser'

openpgp.initWorker({ path: 'static/openpgp.worker.js' })

function Int8ToBuffer (array) {
  var buffer = new ArrayBuffer(array.length)
  var ubuf = new Uint8Array(buffer)
  for (var i = 0; i < ubuf.length; i += 1) {
    ubuf[i] = array[i]
  }
  return buffer
}

export class DecoderException {
  constructor (e) {
    // super(e && e.toString())

    this.exception = e
  }

  toString () {
    return (this.exception ? this.exception.toString() : 'Unknown error')
  }
}

export class InvalidKeyException extends DecoderException {}
export class InvalidPassphraseException extends DecoderException {}
export class DecodingFailedException extends DecoderException {}
export class FileNotFoundException extends DecoderException {}

export default {
  /**
   * Encodes wav file to mp3 format
   * @param {ArrayBuffer} buffer wav data
   */
  wavToMp3 (buffer) {
    let wav = Lame.WavHeader.readHeader(new DataView(buffer))
    let samples = new Int16Array(buffer, wav.dataOffset, wav.dataLen / 2)

    let mp3buffer = []
    let mp3enc = new Lame.Mp3Encoder(wav.channels, wav.sampleRate, samples)

    let remaining = samples.length
    let maxSamples = 1152
    for (let i = 0; remaining >= maxSamples; i += maxSamples) {
      let mono = samples.subarray(i, i + maxSamples)
      let mp3buf = mp3enc.encodeBuffer(mono)
      if (mp3buf.length > 0) {
        mp3buffer.push(mp3buf)
      }
      remaining -= maxSamples
    }

    var d = mp3enc.flush()
    if (d.length) {
      mp3buffer.push(d)
    }

    return mp3buffer
  },

  /**
   * Downloads and possibly decrypt and encode recording.
   *
   * @param {Object} recording recording database object
   * @param {string} privateKey
   * @param {string} passphrase
   * @param {'play'|'download'} action
   * @param {(downloadProgress: number, decodeProgress: number) => void} progressCallback
   * @return {Promise<Blob>}
   */
  decode (recording, privateKey, passphrase, action, progressCallback) {
    return new Promise((resolve, reject) => {
      let privateKeyObj
      let encodeToMp3 = action === 'play' && Browser.isIE() && recording.codecType === 'WAV'

      if (recording.encrypted) {
        // Try to decode private key
        try {
          privateKeyObj = oModule.key.readArmored(privateKey).keys[0]
        } catch (e) {
          return reject(new InvalidKeyException(e))
        }

        // Check if private key requires passphrase
        if (privateKeyObj.primaryKey.encrypted && !passphrase) {
          return reject(new InvalidPassphraseException('Passphrase not provided, but private key is encrypted'))
        }

        // Decrypt private key if passphrase is supplied
        try {
          if (passphrase) {
            privateKeyObj.decrypt(passphrase)
          }
        } catch (e) {
          return reject(new InvalidPassphraseException(e))
        }
      }

      // Load recording from external source
      let promise = FileSystem
        .getRecording(recording.id, action, (loaded, total) => {
          progressCallback && progressCallback(loaded / total, 0)
        })
        .then(result => {
          progressCallback && progressCallback(1, 0)
          return result
        })
        .catch(err => {
          throw new FileNotFoundException(err)
        })

      // Decrypt recording if needed
      if (recording.encrypted) {
        promise = promise
          .then(data => {
            // Prepare decryption object
            return {
              message: oModule.message.read(new Uint8Array(data)),
              privateKey: privateKeyObj,
              format: 'binary'
            }
          })
          .catch(error => {
            throw new DecodingFailedException(error)
          })
          .then(options => {
            // Decrypt the file
            return openpgp.decrypt(options)
          })
          .then(result => {
            return Int8ToBuffer(result.data)
          })
          .catch(error => {
            // Pass decoder exceptions on
            if (error instanceof DecoderException) {
              throw error
            }

            // This means we have encrypted key without passhrase or with wrong passphrase
            if (error.toString() === 'Private key is not decrypted.') {
              throw new InvalidPassphraseException(error)
            }

            // Something occured, most probably key is wrong
            throw new InvalidKeyException(error)
          })
      }

      // Encode to mp3 if needed
      promise = promise
        .then(data => {
          let buffer = [data]

          // Only try to change format if we're playing
          if (encodeToMp3) {
            // Try to decode to MP3 on IE
            try {
              buffer = this.wavToMp3(data)
            } catch (e) {
              throw new DecodingFailedException(e)
            }
          }

          // Return in form of blob
          return new Blob(buffer, { type: 'audio/mp3' })
        })

      resolve(promise)
    })
  }
}



// WEBPACK FOOTER //
// ./src/utils/Decoder.js